import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useRealtime } from '../reception/useRealtimeHook';
import type { RealtimeMessage } from '../components/reception-nav/types';
import { UserProfileContext, type UserProfileContextValue } from './UserProfileContext';
import { QUERY_META } from '../queries/core/queryMeta';
import { receptionKeys } from '../queries/reception/receptionKeys';
import { getReceptionInvalidationsForEvent } from '../queries/realtime/invalidation';
import { scheduleRealtimeInvalidation } from '../queries/realtime/scheduleInvalidation';
import {
  fetchReceptionPendingBadges,
  fetchUserProfileContextBootstrap,
} from '../api/reception';
import { useAuth } from '../auth/useAuth';
import { personName, personNameLocalizations } from '../ui/locale/personNames';
import { useOptionalLanguage } from '../context/LanguageContext';
import type { NameLocalizations } from '../api/localization';

interface UserProfilePayload {
  full_name?: string;
  name_localizations?: NameLocalizations;
  branch?: string;
  role?: string;
  is_general_manager?: boolean;
  can_manage_reception_all_branches?: boolean;
  is_marketing_money?: boolean;
  profile?: {
    full_name?: string;
    name_localizations?: NameLocalizations;
    branch?: string;
    role?: string;
    permissions?: {
      can_approve_finance?: boolean;
    };
  };
  permissions?: {
    can_approve_finance?: boolean;
    finance?: {
      can_approve?: boolean;
    };
  };
}

export const UserProfileProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, authEpoch } = useAuth();
  const locale = useOptionalLanguage();
  const [empName, setEmpName] = useState<string>('');
  const [empNameLocalizations, setEmpNameLocalizations] = useState<NameLocalizations>();
  const [userBranch, setUserBranch] = useState<string>('');
  const [showSuper, setShowSuper] = useState(false);
  const [showLanguage, setShowLanguage] = useState(false);
  const [canApproveFinance, setCanApproveFinance] = useState(false);
  const [isManager, setIsManager] = useState(false);
  const [isGeneralManager, setIsGeneralManager] = useState(false);
  const [isMarketingMoney, setIsMarketingMoney] = useState(false);
  const [appliedProfileIdentity, setAppliedProfileIdentity] = useState('');
  const realtime = useRealtime();
  const queryClient = useQueryClient();
  const profileIdentity = useMemo(() => [
    authEpoch,
    user?.id ?? 'anonymous',
    String(user?.role ?? '').toLowerCase(),
    user?.can_manage_reception_all_branches === true,
    user?.simulation?.active ? 1 : 0,
    user?.simulation?.role ?? '',
    user?.simulation?.target_employee_id ?? '',
  ] as const, [authEpoch, user]);
  const profileIdentityKey = JSON.stringify(profileIdentity);

  const profileQuery = useQuery({
    queryKey: ['profile', 'user-profile-context', ...profileIdentity],
    queryFn: ({ signal }) => fetchUserProfileContextBootstrap(signal),
    retry: 0,
    refetchOnWindowFocus: false,
    meta: QUERY_META.sensitive,
  });

  useLayoutEffect(() => {
    setEmpName('');
    setEmpNameLocalizations(undefined);
    setUserBranch('');
    setShowSuper(false);
    setShowLanguage(false);
    setCanApproveFinance(false);
    setIsManager(false);
    setIsGeneralManager(false);
    setIsMarketingMoney(false);
    setAppliedProfileIdentity('');
  }, [profileIdentity]);

  useEffect(() => {
    if (!profileQuery.isFetched) return;
    if (!profileQuery.data?.mePayload) {
      setAppliedProfileIdentity(profileIdentityKey);
      return;
    }
    const j = profileQuery.data.mePayload as UserProfilePayload;
    const name = j?.full_name || j?.profile?.full_name || '';
    const branch = (j?.profile?.branch || j?.branch || '').toString();
    setEmpName(name);
    setEmpNameLocalizations(personNameLocalizations(j.full_name ? j : j.profile));
    setUserBranch(branch);

    const unifiedRole = String(j?.role || j?.profile?.role || '').trim().toLowerCase();
    const isGM = unifiedRole === 'general_manager' || !!j?.is_general_manager;
    const isSuper = unifiedRole === 'super_reception';
    const hasReceptionGrant = unifiedRole === 'manager' && j?.can_manage_reception_all_branches === true && !user?.simulation?.active;
    const isMgr = unifiedRole === 'manager' || unifiedRole === 'general_manager' || isGM;
    const isMarketingMoneyRole = unifiedRole === 'marketing_money' || !!j?.is_marketing_money;
    const financePermissions =
      (j?.permissions && (j.permissions.can_approve_finance ?? j.permissions.finance?.can_approve)) ??
      j?.profile?.permissions?.can_approve_finance;

    const canApprove = typeof financePermissions === 'boolean' ? financePermissions : isGM || hasReceptionGrant;
    setCanApproveFinance(Boolean(canApprove));
    setIsManager(isMgr);
    setIsGeneralManager(isGM);
    setIsMarketingMoney(isMarketingMoneyRole || isGM);
    setShowSuper(isGM || isSuper || hasReceptionGrant);
    setShowLanguage(isGM || isSuper || hasReceptionGrant);
    setAppliedProfileIdentity(profileIdentityKey);
  }, [profileIdentityKey, profileQuery.data, profileQuery.isFetched, user?.simulation?.active]);

  const profileResolved = profileQuery.isFetched && appliedProfileIdentity === profileIdentityKey;

  const pendingBadgesQuery = useQuery({
    queryKey: [...receptionKeys.super.pendingBadges(), ...profileIdentity],
    // Optional badge availability must not override the authenticated profile scope.
    queryFn: ({ signal }) => fetchReceptionPendingBadges(signal),
    enabled: profileResolved && showSuper,
    retry: 0,
    refetchOnWindowFocus: false,
    meta: QUERY_META.sensitive,
  });

  const pendingDanger = profileResolved && showSuper ? (pendingBadgesQuery.data?.pendingDanger ?? null) : null;
  const pendingAnnouncements = profileResolved && showSuper ? (pendingBadgesQuery.data?.pendingAnnouncements ?? 0) : 0;

  useEffect(() => {
    if (!showSuper) return;
    return realtime.subscribe((ev: RealtimeMessage) => {
      try {
        const eventType = typeof ev.event === 'string' ? ev.event : '';
        if (!eventType) return;
        const targets = getReceptionInvalidationsForEvent(eventType);
        if (targets.length > 0) {
          for (const target of targets) {
            scheduleRealtimeInvalidation(queryClient, target.queryKey);
          }
        }
      } catch {
        // Ignore
      }
    });
  }, [showSuper, realtime, queryClient]);

  const value: UserProfileContextValue = {
    empName,
    empDisplayName: personName({ full_name: empName, name_localizations: empNameLocalizations }, locale?.language ?? 'en'),
    empNameLocalizations,
    userBranch,
    showSuper,
    showLanguage,
    pendingDanger,
    pendingAnnouncements,
    canApproveFinance,
    isManager,
    isGeneralManager,
    isMarketingMoney,
    profileResolved,
  };

  return (
    <UserProfileContext.Provider value={value}>
      {children}
    </UserProfileContext.Provider>
  );
};
